import { ExternalLink } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CreatorModuleYoutubePlayer } from "@/components/learn/creator-module-youtube-player"
import { parseYouTubeId } from "@/lib/youtube"
import type { CreatorLearnModuleContent } from "@/types/learn"

/**
 * OKS-602 — Module content region on `Creator/Learn/ModuleShow`.
 *
 * Renders one of three shapes (UX Spec §2):
 *   - VIDEO       — an HTML5 `<video controls>` fed the CloudFront-signed
 *     `video_stream_url` (signer runs server-side; boundary-mocked in
 *     tests — never a live fetch here).
 *   - YOUTUBE     — OKS-761: a LINK whose `link_url` parses to a YouTube
 *     video id renders an INLINE `<CreatorModuleYoutubePlayer>` (lite-embed,
 *     plays in-page, never a new tab) INSTEAD of the link card. This REPLACES
 *     the card — it is the single affordance (OKS-721), not a second CTA.
 *   - LINK        — an external-link card that opens `link_url` in a new tab
 *     (non-YouTube URL, or a YouTube URL that fails to parse → soft-fail card).
 *   - EMPTY       — a "no content" affordance when neither is present
 *     (misconfigured module; fail-soft, not a crash).
 *
 * The `<video>` has no `autoPlay` — the creator initiates playback; this
 * keeps mobile data + a11y expectations sane.
 *
 * OKS-721 — the LINK card is the SINGLE, deliberate affordance. A second
 * "open" button used to sit below it, gated `hasLink && !hasVideo`. It
 * rendered on every link module — that WAS the bug: two identical CTAs
 * pointing at the same `link_url`, which creators read as two different
 * actions.
 *
 * The `!hasVideo` clause could never discriminate: `MasterclassModule`
 * validates an XOR (`exactly_one_video_source`), so `hasLink` already
 * implies `!hasVideo` and the guard reduced to plain `hasLink`. A
 * deliberate alternative affordance would not carry a clause that cannot
 * change the outcome — the button was an accident, not a fallback.
 *
 * Do NOT re-add a second CTA. The card is the whole target (icon + label
 * + destination URL); its hover surface, focus ring, and URL truncation
 * are load-bearing precisely because it is the only route to the resource.
 */
export interface CreatorModuleContentProps {
  module: CreatorLearnModuleContent
}

export function CreatorModuleContent({ module }: CreatorModuleContentProps) {
  const { t } = useTranslation()

  const hasVideo = module.video_stream_url !== null
  const hasLink = module.link_url !== null
  // OKS-761 — a YouTube link_url reroutes to the inline player; a null id
  // (non-YouTube or malformed) soft-fails through to the link card below.
  const youtubeId = hasLink ? parseYouTubeId(module.link_url) : null

  return (
    <div className="flex flex-col gap-4" data-testid="learn-module-content">
      {module.description && (
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {module.description}
        </p>
      )}

      {hasVideo ? (
        <video
          controls
          preload="metadata"
          src={module.video_stream_url ?? undefined}
          className="aspect-video w-full rounded-md bg-black"
          data-testid="learn-module-video"
        >
          {t("learn.module.video_unsupported")}
        </video>
      ) : youtubeId ? (
        <CreatorModuleYoutubePlayer videoId={youtubeId} title={module.title} />
      ) : hasLink ? (
        <a
          href={module.link_url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:bg-accent focus-visible:ring-ring flex items-center gap-3 rounded-md border p-4 focus:outline-none focus-visible:ring-2"
          data-testid="learn-module-link"
        >
          <ExternalLink
            className="text-muted-foreground size-5 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">
              {t("learn.module.open_link")}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {module.link_url}
            </span>
          </span>
        </a>
      ) : (
        <div
          className="border-input text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm"
          data-testid="learn-module-no-content"
        >
          <p>{t("learn.module.no_content")}</p>
        </div>
      )}
    </div>
  )
}
